import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ButtonLinkComponent } from 'building-blocks/button-link/button-link.component';
import Player from '@vimeo/player';
import YoutubePlayer from 'youtube-player';
import { YouTubePlayer } from 'youtube-player/dist/types';
import type { VimeoEmbedParameters } from '@vimeo/player/types/formats';
import { BehaviorSubject } from 'rxjs';
import { SideBySideImageText, M2A, ButtonLink } from 'www-cms';
import { ImageWithLoaderComponent } from '../components/image-with-loader/image-with-loader.component';
import { BaseBuildingBlockComponent } from '../components/base-building-block.component';
import { LastItemBgClass } from '../../shell/shell.service';
import { PlayButtonOverlayComponent } from '../components/play-button-overlay/play-button-overlay.component';
import { CookieConsentService } from 'cookie-consent/lib/services/cookie-consent.service';
import { SegmentService } from 'segment';
import { DirectusDirective } from 'directus';
import type { SegmentTrackEvent } from 'segment';

@Component({
  selector: 'ss-www-side-by-side-image-text',
  imports: [
    CommonModule,
    ButtonLinkComponent,
    ImageWithLoaderComponent,
    PlayButtonOverlayComponent,
    DirectusDirective,
  ],
  templateUrl: './side-by-side-image-text.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideBySideImageTextComponent
  extends BaseBuildingBlockComponent
  implements AfterViewInit, OnDestroy
{
  private platformId = inject(PLATFORM_ID);
  private readonly consentService = inject(CookieConsentService);
  private readonly segmentService = inject(SegmentService);
  private readonly quartileLabels: Record<number, SegmentTrackEvent> = {
    1: 'Video Content 25%',
    2: 'Video Content 50%',
    3: 'Video Content 75%',
  } as const;

  @Input({ required: true }) sideBySideImageTextData!: SideBySideImageText;
  @Input() disableAnimation = false;
  private player?: Player | YouTubePlayer;

  youTubePoster?: HTMLImageElement;

  @ViewChild('video') video?: ElementRef;

  playClicked$ = new BehaviorSubject(false);

  // Tracking state for video analytics
  private totalLength = 0;
  private firedQuartiles: Set<number> = new Set();
  private hasStarted = false;
  private hasEnded = false;
  private youtubeInterval?: ReturnType<typeof setInterval>;
  private sessionId?: string;

  // Lifecycle hook to initialize video players after the view has been initialized
  ngAfterViewInit(): void {
    if (
      !isPlatformBrowser(this.platformId) ||
      !this.sideBySideImageTextData.video ||
      !this.video
    ) {
      return;
    }
    if (
      this.sideBySideImageTextData.videoProvider === 'vimeo' &&
      !this.sideBySideImageTextData.customVideoThumbnail
    ) {
      const config: VimeoEmbedParameters = {
        id: parseInt(this.sideBySideImageTextData.video),
        autoplay: false,
        controls: false,
        responsive: true,
        background: false,
        dnt: this.consentService.consent().performance,
      };
      this.player = new Player(this.video.nativeElement, config);
    } else if (
      this.sideBySideImageTextData.videoProvider === 'youtube' &&
      !this.sideBySideImageTextData.customVideoThumbnail
    ) {
      // Do not create the YouTube player on init. Instead use poster to avoid red play button inside the player UI
      const container = this.video?.nativeElement as HTMLElement | undefined;
      if (container) {
        const videoId = this.sideBySideImageTextData.video;
        const img = document.createElement('img');
        img.src = `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;
        img.alt = 'Video poster';
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.loading = 'eager';
        img.decoding = 'async';
        this.youTubePoster = img;
        container.appendChild(img);
      }
    }
  }

  ngOnDestroy(): void {
    this.stopTrackingYouTubeTimeUpdates();
    if (this.player) {
      this.player.destroy();
      this.player = undefined;
    }
  }

  buttons(index: number, item: M2A<ButtonLink>) {
    return item.item.id;
  }

  // Handle the big play button click event to initialize and play the video
  bigPlayButtonClicked() {
    this.playClicked$.next(true);
    // If there's no video id configured, do nothing
    if (
      !isPlatformBrowser(this.platformId) ||
      !this.sideBySideImageTextData.video ||
      !this.video
    ) {
      return;
    }

    this.playClicked$.next(true);
    if (this.player) {
      this.player.destroy();
    }

    // Reset tracking state
    this.hasStarted = false;
    this.hasEnded = false;
    this.firedQuartiles.clear();

    // Handle provider-specific creation
    if (this.sideBySideImageTextData.videoProvider === 'vimeo') {
      const config: VimeoEmbedParameters = {
        id: parseInt(this.sideBySideImageTextData.video),
        autoplay: false,
        controls: true,
        responsive: true,
        background: false,
        dnt: this.consentService.consent().performance,
      };
      this.player = new Player(this.video?.nativeElement, config);
      this.setUpVideoTracking(this.player);
      this.player.play();
    } else if (this.sideBySideImageTextData.videoProvider === 'youtube') {
      // Remove poster image if present
      if (this.youTubePoster) {
        this.youTubePoster.remove();
      }
      // create the youtube-player and start playback
      this.player = YoutubePlayer(this.video?.nativeElement, {
        videoId: this.sideBySideImageTextData.video,
        playerVars: {
          autoplay: 0,
          controls: 1,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
        },
        host: 'https://www.youtube-nocookie.com',
      });
      this.setUpVideoTracking(this.player);
      this.player.playVideo();
    }
  }

  override getBgClass(): LastItemBgClass {
    return this.sideBySideImageTextData.invert
      ? 'bg-white'
      : 'bg-ss-www-background';
  }

  // Set up tracking for video events play, quartiles, and completion
  private setUpVideoTracking(player: Player | YouTubePlayer): void {
    if (this.sideBySideImageTextData.videoProvider === 'vimeo') {
      const vimeoPlayer = player as Player;

      vimeoPlayer.getDuration().then((duration) => {
        this.totalLength = duration;
      });

      vimeoPlayer.on('play', () => {
        this.sessionId = crypto.randomUUID();
        //guard against multiple play events firing if user clicks play/pause multiple times
        if (!this.hasStarted) {
          this.segmentService.trackVideoEvent('Video Playback Started', {
            sessionId: this.sessionId,
            videoPlayer: 'vimeo',
            contentAssetId: this.sideBySideImageTextData.video || undefined,
            position: 0,
            totalLength: this.totalLength,
          });
          this.hasStarted = true;
        }
      });

      vimeoPlayer.on('timeupdate', (data: { seconds: number }) => {
        this.checkAndTrackQuartile(data.seconds);
      });

      vimeoPlayer.on('ended', () => {
        // guard to prevent multiple ended events from firing
        if (!this.hasEnded) {
          this.segmentService.trackVideoEvent('Video Content Completed', {
            sessionId: this.sessionId,
            videoPlayer: 'vimeo',
            contentAssetId: this.sideBySideImageTextData.video || undefined,
            position: this.totalLength,
            totalLength: this.totalLength,
          });
          this.hasEnded = true;
        }
      });
    } else if (this.sideBySideImageTextData.videoProvider === 'youtube') {
      const ytPlayer = player as YouTubePlayer;

      ytPlayer.getDuration().then((duration) => {
        this.totalLength = duration;
      });

      // YouTube player state: -1=unstarted, 0=ended, 1=playing, 2=paused, 3=buffering, 5=cued
      ytPlayer.on('stateChange', (event) => {
        const state = event.data;

        if (state === 1 && !this.hasStarted) {
          this.hasStarted = true;
          // Playback started
          this.sessionId = crypto.randomUUID();
          ytPlayer.getCurrentTime().then((position) => {
            this.segmentService.trackVideoEvent('Video Playback Started', {
              sessionId: this.sessionId,
              videoPlayer: 'youtube',
              contentAssetId: this.sideBySideImageTextData.video || undefined,
              position: position,
              totalLength: this.totalLength,
            });
          });
          // Start tracking time updates for quartiles
          this.trackYouTubeTimeUpdates();
        } else if (state === 1) {
          // Resumed playing after pause
          this.trackYouTubeTimeUpdates();
        } else if (state === 0 && !this.hasEnded) {
          // Ended
          this.hasEnded = true;
          this.segmentService.trackVideoEvent('Video Content Completed', {
            sessionId: this.sessionId,
            videoPlayer: 'youtube',
            contentAssetId: this.sideBySideImageTextData.video || undefined,
            position: this.totalLength,
            totalLength: this.totalLength,
          });
        }
      });
    }
  }

  // For YouTube, since there isn't a built-in timeupdate event, we need to poll the current time while the video is playing
  private trackYouTubeTimeUpdates() {
    const ytPlayer = this.player as YouTubePlayer;
    if (this.youtubeInterval !== undefined) {
      return; // Already tracking
    }
    this.youtubeInterval = setInterval(() => {
      ytPlayer.getPlayerState().then((state) => {
        if (state !== 1) {
          // Not playing
          this.stopTrackingYouTubeTimeUpdates();
          return;
        }
        ytPlayer.getCurrentTime().then((position) => {
          this.checkAndTrackQuartile(position);
        });
      });
    }, 1000);
  }

  private stopTrackingYouTubeTimeUpdates() {
    if (this.youtubeInterval) {
      clearInterval(this.youtubeInterval);
      this.youtubeInterval = undefined;
    }
  }

  // Track events when video reaches quartiles (25%, 50%, 75%)
  private checkAndTrackQuartile(position: number): void {
    const quartile = Math.floor((position / this.totalLength) * 4);
    if (quartile > 0 && quartile < 4 && !this.firedQuartiles.has(quartile)) {
      this.firedQuartiles.add(quartile);

      const videoPlayer =
        this.sideBySideImageTextData.videoProvider === 'vimeo'
          ? 'vimeo'
          : 'youtube';

      this.segmentService.trackVideoEvent(this.quartileLabels[quartile], {
        sessionId: this.sessionId,
        videoPlayer,
        contentAssetId: this.sideBySideImageTextData.video || undefined,
        position,
        totalLength: this.totalLength,
      });
    }
  }
}
